import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';

import {
  useMonkeysComponentAttributes,
  useMonkeysLocale,
} from '../../provider/context';

function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(' ');
}

// ---------- Inline SVG Icon ----------

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

// ---------- Component ----------

export interface I18nLanguage {
  code: string;
  label: string;
}

export interface I18nSelectorProps {
  languages: I18nLanguage[];
  currentLanguage: string;
  onLanguageChange: (code: string) => void;
  tooltip?: string;
  className?: string;
}

export function I18nSelector({
  languages,
  currentLanguage,
  onLanguageChange,
  tooltip,
  className,
}: I18nSelectorProps) {
  const locale = useMonkeysLocale();
  const componentAttributes = useMonkeysComponentAttributes();
  const trigger = (
    <DropdownMenu.Trigger asChild>
      <button
        {...componentAttributes}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] transition-colors focus-visible:outline-none',
          className,
        )}
      >
        <GlobeIcon />
        <span className="sr-only">
          {tooltip ?? locale.messages.toolbar.language.switch}
        </span>
      </button>
    </DropdownMenu.Trigger>
  );

  return (
    <DropdownMenu.Root>
      {tooltip ? (
        <Tooltip.Provider delayDuration={300}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="rounded-md bg-[hsl(var(--popover))] px-3 py-1.5 text-xs text-[hsl(var(--popover-foreground))] shadow-md border border-[hsl(var(--border))] animate-in fade-in-0 zoom-in-95"
                sideOffset={5}
              >
                {tooltip}
                <Tooltip.Arrow className="fill-[hsl(var(--popover))]" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        trigger
      )}

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[8rem] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          {languages.map(({ code, label }) => (
            <DropdownMenu.Item
              key={code}
              className={cn(
                'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]',
                currentLanguage === code && 'bg-[hsl(var(--accent))]',
              )}
              onSelect={() => onLanguageChange(code)}
            >
              {label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// ---------- Sub-menu variant (for embedding in user dropdown) ----------

export interface I18nSubMenuProps {
  languages: I18nLanguage[];
  currentLanguage: string;
  onLanguageChange: (code: string) => void;
  label?: string;
}

/**
 * A DropdownMenuSub component for embedding language selector
 * inside a Radix DropdownMenu (e.g. user avatar menu).
 * Must be rendered as a child of DropdownMenu.Group / DropdownMenu.Content.
 */
export function I18nSubMenu({
  languages,
  currentLanguage,
  onLanguageChange,
  label,
}: I18nSubMenuProps) {
  const locale = useMonkeysLocale();
  const componentAttributes = useMonkeysComponentAttributes();
  const fallbackLabel = locale.provided
    ? locale.messages.toolbar.language.label
    : '语言';
  const currentLabel = languages.find((l) => l.code === currentLanguage)?.label ?? currentLanguage;

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        {...componentAttributes}
        className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[state=open]:bg-[hsl(var(--accent))]"
      >
        <GlobeIcon className="h-4 w-4" />
        <span>{label ?? fallbackLabel}</span>
        <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{currentLabel}</span>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="z-50 min-w-[8rem] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95"
          sideOffset={8}
        >
          {languages.map(({ code, label: langLabel }) => (
            <DropdownMenu.CheckboxItem
              key={code}
              checked={currentLanguage === code}
              disabled={currentLanguage === code}
              className={cn(
                'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 pl-7 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              )}
              onCheckedChange={() => onLanguageChange(code)}
            >
              <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              </DropdownMenu.ItemIndicator>
              {langLabel}
            </DropdownMenu.CheckboxItem>
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}
